/*============================================================================
  Per-item gift wrapping for the cart drawer and cart page

  - Each cart line has its own checkbox (rendered in snippets/cart-item.liquid).
  - Ticking it writes line-item properties on that line only:
      Gift Wrapping: Yes
      Gift Product:  <product title from the cart line>
  - The charge is a separate line for the product chosen in Theme settings ->
    Gift Wrapper (line-item properties cannot change price). Each wrapped line
    gets exactly one charge line, linked with the hidden property
    _gift_wrap_for = <parent line key>.
  - sync() runs after every cart render, so quantity changes, removals, adds
    and page loads all converge on the same state (orphaned or missing charge
    lines are fixed automatically).

  Relies on: theme.cart, theme.CartForm, theme.routes (theme.js) and
  theme.giftWrap = { productId, variantId, error } (layout/theme.liquid)
==============================================================================*/
(function() {
  var config = theme.giftWrap;

  if (!config || !config.variantId || !theme.CartForm) {
    return;
  }

  var props = {
    flag: 'Gift Wrapping',
    product: 'Gift Product',
    link: '_gift_wrap_for'
  };

  // Every cart write goes through one queue so rapid clicks never race
  var queue = Promise.resolve();

  function enqueue(task) {
    var run = queue.then(task);
    queue = run.catch(function() {});
    return run;
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json'
      },
      body: JSON.stringify(body)
    }).then(function(response) {
      return response.json().then(function(data) {
        if (!response.ok) {
          throw data;
        }
        return data;
      });
    });
  }

  function isChargeLine(item) {
    return item.product_id === config.productId;
  }

  function isWrapped(item) {
    return !isChargeLine(item) && item.properties && item.properties[props.flag] === 'Yes';
  }

  function rebuildCarts() {
    document.dispatchEvent(new CustomEvent('cart:build'));
  }

  /*============================================================================
    Toggle gift wrapping on a single line
  ==============================================================================*/
  function setGiftWrap(key, enabled) {
    return enqueue(function() {
      return theme.cart.getCart().then(function(cart) {
        var item = cart.items.find(function(line) { return line.key === key; });

        if (!item) {
          return;
        }

        // change.js replaces a line's properties, so start from the existing ones.
        // Blank values are dropped by Shopify, which is how a property is removed.
        var properties = Object.assign({}, item.properties);
        properties[props.flag] = enabled ? 'Yes' : '';
        properties[props.product] = enabled ? item.product_title : '';

        return post(theme.routes.cartChange, {
          id: key,
          quantity: item.quantity,
          properties: properties
        });
      });
    });
  }

  /*============================================================================
    Reconcile charge lines with wrapped lines. Resolves true if the cart changed.
  ==============================================================================*/
  function sync() {
    return enqueue(function() {
      return theme.cart.getCart().then(function(cart) {
        var wrapped = {};
        var covered = {};
        var updates = {};
        var additions = [];

        cart.items.forEach(function(item) {
          if (isWrapped(item)) {
            wrapped[item.key] = item;
          }
        });

        cart.items.filter(isChargeLine).forEach(function(charge) {
          var target = charge.properties && charge.properties[props.link];

          if (wrapped[target] && !covered[target]) {
            covered[target] = true;
            if (charge.quantity !== 1) {
              updates[charge.key] = 1;
            }
          } else {
            // Parent removed, unchecked, or duplicate charge
            updates[charge.key] = 0;
          }
        });

        Object.keys(wrapped).forEach(function(key) {
          if (covered[key]) {
            return;
          }
          var charge = { id: config.variantId, quantity: 1, properties: {} };
          charge.properties[props.product] = wrapped[key].product_title;
          charge.properties[props.link] = key;
          additions.push(charge);
        });

        var request = Promise.resolve();

        if (Object.keys(updates).length) {
          request = request.then(function() {
            return post('/cart/update.js', { updates: updates });
          });
        }

        if (additions.length) {
          request = request.then(function() {
            return post(theme.routes.cartAdd, { items: additions });
          }).catch(function(error) {
            // Charge could not be added (e.g. unavailable): unwrap so the
            // checkbox never shows a gift wrap that isn't being charged.
            return revertWrapped(Object.keys(wrapped).filter(function(key) { return !covered[key]; }), cart)
              .then(function() {
                alert(config.error || (error && error.description) || 'Gift wrapping is unavailable.');
              });
          });
        }

        return request.then(function() {
          return Object.keys(updates).length > 0 || additions.length > 0;
        });
      });
    });
  }

  function revertWrapped(keys, cart) {
    return keys.reduce(function(chain, key) {
      var item = cart.items.find(function(line) { return line.key === key; });
      var properties = Object.assign({}, item && item.properties);
      properties[props.flag] = '';
      properties[props.product] = '';

      return chain.then(function() {
        return post(theme.routes.cartChange, { id: key, quantity: item.quantity, properties: properties });
      });
    }, Promise.resolve());
  }

  /*============================================================================
    Wire up: checkbox changes + resync after every cart render
  ==============================================================================*/
  document.addEventListener('change', function(evt) {
    var input = evt.target;

    if (!input.matches || !input.matches('[data-gift-wrap-toggle]')) {
      return;
    }

    var wrapper = input.closest('.cart__gift-wrap');
    input.disabled = true;
    if (wrapper) { wrapper.classList.add('is-loading'); }

    setGiftWrap(input.dataset.lineKey, input.checked)
      .then(sync)
      .catch(function(error) {
        alert((error && error.description) || config.error);
      })
      .then(rebuildCarts);
  });

  var originalCartMarkup = theme.CartForm.prototype.cartMarkup;

  theme.CartForm.prototype.cartMarkup = function(text) {
    originalCartMarkup.call(this, text);

    sync().then(function(changed) {
      if (changed) {
        rebuildCarts();
      }
    }).catch(function() {});
  };

  // The cart page has no drawer build on load, so reconcile once here
  // (e.g. after the "Remove" link redirects to /cart) and refresh if fixed.
  document.addEventListener('DOMContentLoaded', function() {
    if (!document.body.classList.contains('template-cart')) {
      return;
    }

    sync().then(function(changed) {
      if (changed) {
        window.location.reload();
      }
    }).catch(function() {});
  });

  theme.giftWrap = Object.assign(config, { sync: sync, setGiftWrap: setGiftWrap });
})();
